import React, { HTMLAttributes, useCallback, useContext } from 'react'
import OfferFilterSelectBox from '../OfferFilterSelectBox'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'
import { isFlashHotel } from 'lib/offer/offerTypes'

interface Props extends HTMLAttributes<HTMLButtonElement> {
  airport?: App.Airport;
  offer?: App.Offer;
  activeFilter?: string;
  offerType: App.LEContractedAnalyticsType;
  inSidebar?: boolean;
  iconDisabled?: boolean;
}

const OfferAirportSelectBox = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    airport,
    activeFilter,
    offerType,
    offer,
    iconDisabled,
    ...rest
  } = props

  const dispatch = useOfferPageDispatch()
  const isOpen = activeFilter === 'airport'
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const onBoxClick = useCallback(() => {
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('AIRPORT_FILTER_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
      trackEvent('AIRPORT_FILTER_PRESSED')
      if (isFlashHotel(offer)) {
        Analytics.track('client-event', clientEvent({
          subject: 'airport',
          action: 'click',
          category: 'offer_funnel',
          type: 'interaction',
        }))
      }
    }
  }, [isOpen, dispatch, trackEvent, offer])

  return (<OfferFilterSelectBox
    {...rest}
    data-testid="origin-select"
    ref={ref}
    onClick={onBoxClick}
    active={isOpen}
    icon={!iconDisabled && <LinePlaneDepartureIcon />}
    label={`Departure airport ${offer?.bundledWithFlightsOnly ? '' : '(optional)'}`}
    value={airport ? <b>{airport.name} ({airport.code.toUpperCase()})</b> : undefined}
    placeholder="Select by airport"
  />
  )
})

OfferAirportSelectBox.displayName = 'OfferAirportSelectBox'

export default withOfferPageState(
  (state) => ({
    airport: state.airport,
    activeFilter: state.activeFilter,
    offer: state.offer,
    offerType: state.offer?.analyticsType || state.bedbankOffer?.analyticsType,
  }), true)(OfferAirportSelectBox)
